import { Client } from '@microsoft/microsoft-graph-client';
import { pca } from '../auth/msalConfig';
import { graphRequest } from '../auth/msalConfig';

// Shared in-flight promise for the interactive (redirect) token flow.
// MSAL only tolerates one interactive acquisition at a time — if a
// second caller starts its own redirect while one is already pending,
// the library throws `interaction_in_progress`. This became visible
// once the Sales page started loading four SharePoint lists
// concurrently: when the silent token was expired, each list fired
// its own interactive flow and three of them raced. Serializing here
// means every caller awaits the same flow and shares its result.
let pendingInteractive = null;

/**
 * Acquires a Graph access token silently, falling back to a full-page
 * redirect if interaction is required. MSAL decides when to refresh —
 * we don't cache the token ourselves.
 *
 * Why redirect (not popup): browsers (Chrome, Edge, Safari) block
 * popups that aren't tied to a user click, and the silent-refresh
 * fallback happens in the background without a user click. That
 * surfaced as `popup_window_error` on every data-loading page once a
 * session's token expired. `acquireTokenRedirect` navigates the tab
 * to Microsoft's sign-in page (browsers don't block redirects), and
 * MSAL automatically picks the user back up when they return via
 * handleRedirectPromise in auth/AuthProvider.jsx.
 *
 * Interactive attempts are de-duplicated across concurrent callers
 * via the `pendingInteractive` promise above, so four lists loading
 * at once only ever kick off one redirect.
 */
async function getAccessToken() {
  const account = pca.getActiveAccount() || pca.getAllAccounts()[0];
  if (!account) {
    throw new Error('No signed-in account; cannot call Microsoft Graph.');
  }

  try {
    const result = await pca.acquireTokenSilent({
      ...graphRequest,
      account,
    });
    return result.accessToken;
  } catch (err) {
    // Interaction required — kick off a full-page redirect so we
    // don't get blocked by popup-blockers. acquireTokenRedirect
    // navigates away and never resolves in this tab; the caller's
    // await will hang until the browser leaves the page, then the
    // portal re-initialises when the user returns.
    if (!pendingInteractive) {
      pendingInteractive = pca
        .acquireTokenRedirect(graphRequest)
        .finally(() => {
          pendingInteractive = null;
        });
    }
    const result = await pendingInteractive;
    // Redirect normally navigates before we reach this line; if MSAL
    // ever resolves synchronously (e.g. cached account) we still have
    // a token to hand back.
    return result?.accessToken;
  }
}

/** Shared Graph SDK client. Reuses MSAL token cache under the hood. */
export const graph = Client.init({
  authProvider: async (done) => {
    try {
      const token = await getAccessToken();
      done(null, token);
    } catch (err) {
      done(err, null);
    }
  },
});

export { getAccessToken };
